import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Loader2 } from "lucide-react";
import { postNewsletter } from "../lib/api";
import { toast } from "sonner";

export default function ExitIntent() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("mfc-exit-shown")) return;
    let armed = false;
    const t = setTimeout(() => { armed = true; }, 8000); // arm after 8s
    const onLeave = (e) => {
      if (armed && e.clientY <= 0 && !sessionStorage.getItem("mfc-exit-shown")) {
        setOpen(true);
        sessionStorage.setItem("mfc-exit-shown", "1");
      }
    };
    document.addEventListener("mouseout", onLeave);
    return () => { clearTimeout(t); document.removeEventListener("mouseout", onLeave); };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await postNewsletter({ email });
      toast.success("Sent! Check your inbox for the free guide.");
      setOpen(false);
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center px-6 bg-black/70 backdrop-blur-sm"
          onClick={() => setOpen(false)} data-testid="exit-intent-overlay"
        >
          <motion.div
            initial={{ scale: 0.9, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 30 }}
            transition={{ type: "spring", damping: 24, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="exit-intent-modal"
            className="relative w-full max-w-lg rounded-3xl overflow-hidden glass"
          >
            <button onClick={() => setOpen(false)} data-testid="exit-intent-close" aria-label="Close"
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-black/40 flex items-center justify-center text-white hover:bg-black/60">
              <X className="w-4 h-4" />
            </button>
            <div className="grid md:grid-cols-2">
              <div className="hidden md:block relative">
                <img src="https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=600&q=80" alt="Fat loss guide" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background/80" />
              </div>
              <div className="p-8">
                <span className="text-xs tracking-[0.2em] uppercase font-bold text-accent">Free Guide</span>
                <h3 className="font-heading font-black text-2xl leading-tight mt-3 mb-3">Wait — grab your free Beginner Fat-Loss Guide</h3>
                <p className="text-sm text-muted-foreground mb-6">The exact framework my clients use to lose fat without crash diets. Enter your email and I&apos;ll send it over.</p>
                <form onSubmit={submit} className="space-y-3">
                  <input type="email" required placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)}
                    data-testid="exit-intent-email"
                    className="w-full bg-secondary/60 border border-border rounded-xl px-4 py-3 text-sm outline-none focus:border-accent transition-colors" />
                  <button type="submit" disabled={loading} data-testid="exit-intent-submit"
                    className="w-full rounded-full bg-accent text-white py-3 font-semibold flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-60">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4" /> Send Me The Guide</>}
                  </button>
                </form>
                <p className="text-[11px] text-muted-foreground mt-3 text-center">No spam. Unsubscribe anytime.</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
